import React from "react";
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
  useParams,
} from "react-router-dom";

import LabPractice from "../components/mentorComponents/labPractice/LabPractice";
import DailyPractice from "../components/mentorComponents/dailyPractice/DailyPractice";
import Attendance from "../components/mentorComponents/attendance/Attendance";
import AdminDashboard from "../components/adminComponents/AdminDashboard";
import MentorDashboard from "../components/mentorComponents/MentorDashboard";
import COE from "../components/adminComponents/COE/COE";
import PracticeHead from "../components/adminComponents/PracticeHead/PracticeHead";
import PracticeMentor from "../components/adminComponents/PracticeMentor/PracticeMentor";
import MakerPlan from "../components/adminComponents/MakerPlan/MakerPlan";
import MakerModule from "../components/adminComponents/MakerModel/MakerModule";
import Preview from "../components/adminComponents/preview/Preview";
import DailyReport from "../components/dailyReport/DailyReport";
import MentorAttendanceReport from "../components/dailyReport/mentorReport/MentorAttendanceReport";
import PracticeNotes from "../components/mentorComponents/practiceNotes/PracticeNotes";
import { useMediaQuery } from "@mui/material";
import LandingPage from "../components/mentorComponents/mobileview/LandingPage";
import DailyAttendanceDashboard from "../components/mentorComponents/mobileview/DailyAttendanceDashboard";
import DailyPracticeDashboard from "../components/mentorComponents/mobileview/DailyPracticeDashboard";
import DailyPracticeNotesDashboard from "../components/mentorComponents/mobileview/DailyPracticeNotesDashboard";
import AdminAttendanceReport from "../components/dailyReport/adminReport/AdminAttendanceReport";
import MentorDailyPracticeReport from "../components/dailyReport/mentorReport/MentorDailyPracticeReport";
import CoderDistribution from "../components/dailyReport/coderDistribution/CoderDistribution";
import LabwiseModule from "../components/dailyReport/adminReport/LabwiseModule";
import CheckInOutReport from "../components/dailyReport/adminReport/CheckInOutReport";
import AdminDailyPracticeReport from "../components/dailyReport/adminReport/AdminDailyPracticeReport";
import LearnerReview from "../components/mentorComponents/mobileview/learnerReview/learnerReview";
import LearnerReviewDetails from "../components/mentorComponents/mobileview/learnerReview/learnerReviewDetails";
import LearnerList from "../components/mentorComponents/learnerList/LearnerList";
import MentorReview from "../components/mentorComponents/mentorReview/MentorReview";
import LearnerPractices from "../components/mentorComponents/learnerPractices/LearnerPractices";
import LearnerPracticeDetails from "../components/mentorComponents/learnerPractices/submissionDetails/SubmissionDetails";
import SubmissionDetails from "../components/mentorComponents/mentorReview/submissionDetails/SubmissionDetails";
import TlcPage from "../components/mentorComponents/mentorReview/submissionDetails/TlcPage";
import ChatbotLayout from "../components/chatbot/chatbotLayout";
import MentorLogin from "../pages/MentorLogin";
import AdminLogin from "../pages/AdminLogin";
import LabComponent from "../components/adminComponents/LabComponent/LabComponent";
import AddAdmin from "../components/adminComponents/AddAdmin/AddAdmin";
import PrivateRoute from "./PrivateRoute";
import RoleProtectedRoute from "./RoleProtectedRoute";
import RabbitmqDashboardPage from "../components/adminComponents/rabbitmq/RabbitmqDashboardPage";
import ReportViewerDashboard from "../components/reportViewerComponents/ReportViewerDashboard";
import ReportViewerReview from "../components/reportViewerComponents/ReportViewerReview";
import ReportViewerAttendance from "../components/reportViewerComponents/ReportViewerAttendance";

const CoeViewerAuthWrapper = () => {
  const token = localStorage.getItem("token");
  const user = JSON.parse(localStorage.getItem("user") || "null");
  const { coeName } = useParams();

  if (!token || user?.role !== "coe_report_viewer") {
    return <AdminLogin />;
  }

  if (coeName && !["admin", "mentor", "college-viewer"].includes(coeName)) {
    const normalize = (val) => (val ? val.trim().toLowerCase().replace(/[\s-_]+/g, "") : "");
    const userCoe = user.coeName || user.coe_name || "";
    if (normalize(userCoe) !== normalize(coeName)) {
      const formattedUserCoe = userCoe ? userCoe.trim().replace(/\s+/g, "-") : "coe";
      return <Navigate to={`/${formattedUserCoe}/learner-reviews`} replace />;
    }
  }

  return <ReportViewerDashboard />;
};

function RoutingModule() {
  const isMobile = useMediaQuery("(max-width:600px)");

  const AppRoutes = createBrowserRouter([
    {
      path: "/",
      element: <Navigate to="/mentor" replace />,
    },
    {
      path: "/mentor",
      element: <ChatbotLayout />,
      children: [
        { path: "", element: <MentorLogin /> },
        {
          path: "",
          element: (
            <PrivateRoute>
              <MentorDashboard />
            </PrivateRoute>
          ),
          children: [
            {
              path: "lab-practice",
              element: !isMobile ? <LabPractice /> : <LandingPage />,
            },
            {
              path: "daily-practice",
              element: !isMobile ? <DailyPractice /> : <DailyPracticeDashboard />,
            },
            {
              path: "practice-notes",
              element: !isMobile ? <PracticeNotes /> : <DailyPracticeNotesDashboard />,
            },
            {
              path: "attendance",
              element: !isMobile ? <Attendance /> : <DailyAttendanceDashboard />,
            },
            { path: "coder-distribution", element: <CoderDistribution /> },
            { path: "learners", element: <LearnerList /> },
            {
              path: "daily-report",
              element: <DailyReport />,
              children: [
                { path: "practice", element: <MentorDailyPracticeReport /> },
                { path: "attendance", element: <MentorAttendanceReport /> },
              ],
            },
            {
              path: "review",
              children: [
                { path: "learner/:learner_code", element: <LearnerReview /> },
                { path: "learner/topic-review", element: <LearnerReviewDetails /> },
              ],
            },
            { path: "learner-reviews", element: <MentorReview /> },
            { path: "learner-reviews/:submissionId", element: <SubmissionDetails /> },
            { path: "learner-practices", element: <LearnerPractices /> },
            { path: "learner-practices/:submissionId", element: <LearnerPracticeDetails /> },
            { path: "learner-reviews/:submissionId/tlc", element: <TlcPage /> },
          ],
        },
      ],
    },
    {
      path: "/admin",
      element: <ChatbotLayout />,
      children: [
        { path: "", element: <AdminLogin /> },
        {
          path: "",
          element: (
            <PrivateRoute>
              <AdminDashboard />
            </PrivateRoute>
          ),
          children: [
            {
              path: "add-admin",
              element: (
                <RoleProtectedRoute allowedRoles={["super_admin"]}>
                  <AddAdmin />
                </RoleProtectedRoute>
              ),
            },
            {
              path: "coe-center",
              element: (
                <RoleProtectedRoute allowedRoles={["super_admin", "coe_admin"]}>
                  <COE />
                </RoleProtectedRoute>
              ),
            },
            {
              path: "lab-practice",
              element: (
                <RoleProtectedRoute allowedRoles={["super_admin", "coe_admin"]}>
                  <LabComponent />
                </RoleProtectedRoute>
              ),
            },
            {
              path: "learner-reviews",
              element: (
                <RoleProtectedRoute allowedRoles={["super_admin", "coe_admin", "coe_report_admin"]}>
                  <MentorReview isAdmin={true} />
                </RoleProtectedRoute>
              ),
            },
            {
              path: "learner-practices",
              element: (
                <RoleProtectedRoute allowedRoles={["super_admin", "coe_admin", "coe_report_admin"]}>
                  <LearnerPractices isAdmin={true} />
                </RoleProtectedRoute>
              ),
            },
            {
              path: "learner-practices/:submissionId",
              element: (
                <RoleProtectedRoute allowedRoles={["super_admin", "coe_admin", "coe_report_admin"]}>
                  <LearnerPracticeDetails isAdmin={true} />
                </RoleProtectedRoute>
              ),
            },
            {
              path: "maker-module",
              element: (
                <RoleProtectedRoute allowedRoles={["super_admin", "coe_admin"]}>
                  <MakerModule />
                </RoleProtectedRoute>
              ),
            },
            {
              path: "maker-plan",
              element: (
                <RoleProtectedRoute allowedRoles={["super_admin", "coe_admin"]}>
                  <MakerPlan />
                </RoleProtectedRoute>
              ),
            },
            {
              path: "practice-head",
              element: (
                <RoleProtectedRoute allowedRoles={["super_admin", "coe_admin"]}>
                  <PracticeHead />
                </RoleProtectedRoute>
              ),
            },
            {
              path: "practice-mentor",
              element: (
                <RoleProtectedRoute allowedRoles={["super_admin", "coe_admin"]}>
                  <PracticeMentor />
                </RoleProtectedRoute>
              ),
            },
            {
              path: "preview",
              element: (
                <RoleProtectedRoute allowedRoles={["super_admin", "coe_admin"]}>
                  <Preview />
                </RoleProtectedRoute>
              ),
            },
            { path: "coder-distribution", element: <CoderDistribution /> },
            { path: "checkIn-out-report", element: <CheckInOutReport /> },
            {
              path: "qms",
              element: (
                <RoleProtectedRoute allowedRoles={["super_admin", "coe_admin", "coe_report_admin"]}>
                  <RabbitmqDashboardPage />
                </RoleProtectedRoute>
              ),
            },
            {
              path: "daily-report",
              element: <DailyReport />,
              children: [
                { path: "practice", element: <AdminDailyPracticeReport /> },
                { path: "attendance", element: <AdminAttendanceReport /> },
              ],
            },
            { path: "lab-report", element: <LabwiseModule /> },
            { path: "learner-reviews", element: <MentorReview isAdmin={true} /> },
            { path: "learner-reviews/:submissionId", element: <SubmissionDetails isAdmin={true} /> },
            { path: "learner-reviews/:submissionId/tlc", element: <TlcPage isAdmin={true} /> },
          ],
        },
      ],
    },
    {
      path: "/college-viewer",
      element: <ChatbotLayout />,
      children: [
        { path: "", element: <AdminLogin /> },
      ],
    },
    {
      path: "/:coeName",
      element: <ChatbotLayout />,
      children: [
        {
          path: "",
          element: <CoeViewerAuthWrapper />,
          children: [
            {
              index: true,
              element: <Navigate to="learner-reviews" replace />,
            },
            {
              path: "learner-reviews",
              element: (
                <RoleProtectedRoute allowedRoles={["coe_report_viewer"]}>
                  <ReportViewerReview />
                </RoleProtectedRoute>
              ),
            },
            {
              path: "learner-reviews/:submissionId",
              element: (
                <RoleProtectedRoute allowedRoles={["coe_report_viewer"]}>
                  <SubmissionDetails isAdmin={true} />
                </RoleProtectedRoute>
              ),
            },
            {
              path: "learner-reviews/:submissionId/tlc",
              element: (
                <RoleProtectedRoute allowedRoles={["coe_report_viewer"]}>
                  <TlcPage isAdmin={true} />
                </RoleProtectedRoute>
              ),
            },
            {
              path: "learner-attendance",
              element: (
                <RoleProtectedRoute allowedRoles={["coe_report_viewer"]}>
                  <ReportViewerAttendance />
                </RoleProtectedRoute>
              ),
            },
            {
              path: "daily-report/attendance",
              element: <Navigate to="../learner-attendance" replace />,
            },
            {
              path: "attendance",
              element: <Navigate to="../learner-attendance" replace />,
            },
          ],
        },
      ],
    },
  ]);

  return <RouterProvider router={AppRoutes} />;
}

export default RoutingModule;
